import { useState, useEffect } from 'react';
import { format, minutesToHours, addDays, subWeeks, subMonths } from 'date-fns';
import {
  TableHead,
  TableRow,
  TableBody,
  Grid,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  GeneralTable,
  CustomTableCell,
  CustomTableRow,
  QueryContainer,
  SelectDropdown,
  ContainedButton,
  OutlinedButton,
  CustomRangeModal,
} from '../../index';
import {
  sortByCheckInDate,
  sortByCheckOutDate,
  sortByComputedTime,
} from '../../../utilities/sortBy';

export const TimesheetTable = (props) => {
  const [entryPerPage, setEntryPerPage] = useState(10);
  const timeFormat = 'hh:mm:ss a';
  const data = props.data;
  const workSetup = { 1: 'On-site', 2: 'WFH', 3: 'Deployed' };
  const [timesheetList, setTimesheetList] = useState([]);
  const [slice, setSlice] = useState([0, entryPerPage]);
  const [pageCount, setPageCount] = useState(
    Math.floor(data.length / entryPerPage)
  );
  const [sortBy, setSortBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [dateRange, setDateRange] = useState('');
  const [filter, setFilter] = useState([]);
  const [pickerModalOpen, setPickerModalOpen] = useState(false);
  const [startValue, setStartValue] = useState(null);
  const [endValue, setEndValue] = useState(null);
  const [isDescending, setIsDescending] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  //Reset to page 1 when changing the number of entries
  useEffect(() => {
    setCurrentPage(1);
    setSlice([0, entryPerPage]);
  }, [entryPerPage, timesheetList]);

  useEffect(() => {
    if (dateRange === '') {
      setTimesheetList(sortByCheckInDate(data));
      setIsDescending(true);
    }
    setPageCount(Math.floor(timesheetList.length / entryPerPage));
  }, [data, timesheetList, dateRange, filter, entryPerPage]);

  const handlePageChange = (e, page) => {
    setCurrentPage(page);
    let currPage = page - 1;

    page === 1
      ? setSlice([0, entryPerPage])
      : setSlice([currPage * entryPerPage, page * entryPerPage]);
  };

  const handlePickerModalClose = () => {
    setTimesheetList(sortByCheckInDate(data));
    setPickerModalOpen(false);
  };

  const handleCustomDate = () => {
    const filterDate = data.filter((a) => {
      const date = new Date(a.checkin);

      return (
        date >= new Date(startValue) && date <= addDays(new Date(endValue), 1)
      );
    });

    setTimesheetList(filterDate.reverse());
    setPickerModalOpen(false);
  };

  const descendingOrder = (val) => {
    setIsDescending(true);
    if (val === 'checkin' || val === 'date') {
      setTimesheetList(sortByCheckInDate(timesheetList));
    } else if (val === 'checkout') {
      setTimesheetList(sortByCheckOutDate(timesheetList));
    } else if (val === 'time') {
      setTimesheetList(sortByComputedTime(timesheetList));
    }
  };

  const handleOrderChange = (event) => {
    let val = event.target.value;
    setOrder(val);

    if (val === 'asc' && isDescending) {
      setTimesheetList(timesheetList.reverse());
      setIsDescending(false);
    } else if (val === 'desc' && sortBy === '' && dateRange === '') {
      setTimesheetList(sortByCheckInDate(data));
      setIsDescending(true);
    } else if (val === 'desc' && sortBy === '' && dateRange !== '') {
      setTimesheetList(sortByCheckInDate(timesheetList));
      setIsDescending(true);
    } else if (val === 'desc' && sortBy !== '') {
      descendingOrder(sortBy);
    }

    setCurrentPage(1);
    setSlice([0, entryPerPage]);
  };

  const handleSortByChange = (event) => {
    let val = event.target.value;
    setSortBy(val);

    // descending default
    if (val === 'checkin' || val === 'date') {
      isDescending == true
        ? setTimesheetList(sortByCheckInDate(timesheetList))
        : setTimesheetList(sortByCheckInDate(timesheetList).reverse());
    } else if (val === 'time') {
      isDescending == true
        ? setTimesheetList(sortByComputedTime(timesheetList))
        : setTimesheetList(sortByComputedTime(timesheetList).reverse());
    } else if (val === 'checkout') {
      isDescending == true
        ? setTimesheetList(sortByCheckOutDate(timesheetList))
        : setTimesheetList(sortByCheckOutDate(timesheetList).reverse());
    }

    setCurrentPage(1);
    setSlice([0, entryPerPage]);
  };

  const handleDateRangeChange = (event) => {
    let val = event.target.value;
    setDateRange(val);
    const today = new Date();
    const oneWeek = subWeeks(today, 1);
    const twoWeeks = subWeeks(today, 2);
    const oneMonth = subMonths(today, 1);
    const threeMonths = subMonths(today, 3);
    let startDate = new Date();
    let endDate = new Date();

    const filterDate = data.filter((a) => {
      const date = new Date(a.checkin);
      endDate = today;

      val === '3months'
        ? (startDate = threeMonths)
        : val === '30days'
          ? (startDate = oneMonth)
          : val === '14days'
            ? (startDate = twoWeeks)
            : val === '7days'
              ? (startDate = oneWeek)
              : setPickerModalOpen(true);

      return date >= startDate && date <= endDate;
    });

    setTimesheetList(filterDate);
    setFilter(filterDate);

    if (val === 'cutoff') {
      setTimesheetList(props.cutoff);
    }
  };

  const handleReset = () => {
    setTimesheetList(sortByCheckInDate(data));
    setSortBy('');
    setOrder('');
    setDateRange('');
  };

  return (
    <>
      {props.isCutoffTab ? (
        <></>
      ) : (
        <QueryContainer>
          <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
            <Grid item>
              <SelectDropdown
                sx={{ minWidth: 200 }}
                size="small"
                label="Sort by"
                value={sortBy}
                onChange={handleSortByChange}
              >
                <MenuItem value={'date'}>Date</MenuItem>
                {/* [EPSv5_Timesheet-SortBy] Commented out. Will not be implemented*/}
                {/* <MenuItem value={"checkin"}>Check In</MenuItem>
                <MenuItem value={"checkout"}>Check Out</MenuItem> */}
                <MenuItem value={'time'}>Computed Time</MenuItem>
              </SelectDropdown>
            </Grid>
            <Grid item sx={{ ml: 3 }}>
              <SelectDropdown
                sx={{ minWidth: 200 }}
                size="small"
                label="Order"
                value={order}
                onChange={handleOrderChange}
              >
                <MenuItem value={'asc'}>Ascending</MenuItem>
                <MenuItem value={'desc'}>Descending</MenuItem>
              </SelectDropdown>
            </Grid>
            {/*} [EPSv5_Dashboard-timesheetDropDown]
            <Grid item sx={{ ml: 3 }}>
              <SelectDropdown
                sx={{ minWidth: 200 }}
                size="small"
                label="Date range"
                value={dateRange}
                onChange={handleDateRangeChange}
              >
                <MenuItem value={"7days"}>Last 7 days</MenuItem>
                <MenuItem value={"14days"}>Last 14 days</MenuItem>
                <MenuItem value={"30days"}>Last 30 days</MenuItem>
                <MenuItem value={"3months"}>Last 3 months</MenuItem>
              </SelectDropdown>
            </Grid>
      */}
            <Grid item sx={{ ml: 3 }}>
              <OutlinedButton
                btnTitle="Input Date Range"
                onClick={props.primaryButton}
              />
            </Grid>
            {/*
            <Grid item sx={{ ml: 3 }}>
              <OutlinedButton btnTitle="Reset Query" onClick={handleReset} />
            </Grid>
    */}
          </Grid>
        </QueryContainer>
      )}
      {data.length !== 0 ? (
        <GeneralTable
          sx={{
            borderRadius: '5px',
            boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
            mb: 3.6,
          }}
          size="small"
          length={data.length}
          entryPerPage={entryPerPage}
          page={currentPage}
          pageCount={pageCount}
          handlePageChange={handlePageChange}
          entriesPerPageComponent={
            <SelectDropdown
              sx={{ minWidth: 80 }}
              size="small"
              label="Entries"
              value={entryPerPage}
              onChange={(e) => setEntryPerPage(e.target.value)}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </SelectDropdown>
          }
        >
          <TableHead>
            <TableRow>
              <CustomTableCell align="left" value="Date" />
              <CustomTableCell align="left" value="Check In" />
              <CustomTableCell align="left" value="Check Out" />
              <CustomTableCell align="left" value="Computed Time" />
              <CustomTableCell align="left" value="Total Hours" />
              <CustomTableCell align="left" value="Work Setup" />
              <CustomTableCell align="left" value="Work Description" />
            </TableRow>
          </TableHead>
          <TableBody>
            {timesheetList.slice(slice[0], slice[1]).map((ts, index) => (
              <CustomTableRow key={ts._id}>
                <CustomTableCell
                  value={format(new Date(ts.checkin), 'dd MMM yyyy')}
                />
                <CustomTableCell
                  value={format(new Date(ts.checkin), timeFormat)}
                />
                {
                  <CustomTableCell
                    value={
                      ts.checkout === null
                        ? ' --:--:-- '
                        : format(new Date(ts.checkout), timeFormat)
                    }
                  />
                }
                <CustomTableCell
                  value={Math.floor((ts.roundedtime / 60) * 1000) / 1000}
                />
                <CustomTableCell
                  value={`${minutesToHours(ts.roundedtime)} hours ${
                    Math.floor((ts.roundedtime % 60) * 1000000) / 1000000
                  } minutes`}
                />
                <CustomTableCell value={workSetup[ts.worksetup]} />
                <CustomTableCell value={ts.workdesc} />
              </CustomTableRow>
            ))}
          </TableBody>
        </GeneralTable>
      ) : (
        <Typography align="center" variant="subtitle1" sx={{ mt: 2 }}>
          No Data
        </Typography>
      )}
      <CustomRangeModal
        open={pickerModalOpen}
        onClose={handlePickerModalClose}
        startValue={startValue}
        startOnChange={(newValue) => setStartValue(newValue)}
        endValue={endValue}
        endOnChange={(newValue) => setEndValue(newValue)}
        primaryClick={handleCustomDate}
        secondaryClick={handlePickerModalClose}
      />
    </>
  );
};
